// src/TimetableView.js
// Основное расписание из МЭШ в понятном виде: по классу, учителю или кабинету.
// Сетка «дни × уроки», подгруппы в одной клетке друг под другом,
// текущий день и идущий урок подсвечены. На телефоне — список по дням.
import React, { useEffect, useMemo, useState } from 'react';
import {
  DAY_NAMES,
  buildGrid,
  compareClasses,
  compareRooms,
  dayStatus,
  moscowNow,
} from './timetableUtils';
import './TimetableView.css';

const MODES = [
  { id: 'class', title: 'По классам' },
  { id: 'teacher', title: 'По учителям' },
  { id: 'room', title: 'По кабинетам' },
];

const STORAGE_KEY = 'eu_timetable_view';

const readSaved = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch (e) {
    return {};
  }
};

const keyOf = (mode, lesson) =>
  mode === 'teacher' ? lesson.teacher : mode === 'room' ? lesson.room : lesson.class_name;

// «каб. 2.33», но «Зал ФЗК» — без «каб.»
const roomLabel = (room) => (/^\d/.test(room) ? `каб. ${room}` : room);

// Что показывать в клетке, кроме предмета: то, чего нет в заголовке
function details(mode, l) {
  if (mode === 'class') return [l.teacher, l.room && roomLabel(l.room)];
  if (mode === 'teacher') return [l.class_name, l.room && roomLabel(l.room)];
  return [l.class_name, l.teacher];
}

function LessonCell({ mode, lessons }) {
  return lessons.map((l, i) => (
    <div className="tt-lesson" key={i}>
      <div className="tt-subject">{l.subject}</div>
      <div className="tt-details">{details(mode, l).filter(Boolean).join(' · ')}</div>
    </div>
  ));
}

export default function TimetableView({ timetable }) {
  const saved = useMemo(readSaved, []);
  const [mode, setMode] = useState(saved.mode || 'class');
  const [selected, setSelected] = useState(saved.selected || {});
  const [now, setNow] = useState(() => moscowNow());

  // Подсветку текущего урока обновляем раз в 20 секунд — этого достаточно
  useEffect(() => {
    const timer = setInterval(() => setNow(moscowNow()), 20000);
    return () => clearInterval(timer);
  }, []);

  const lessons = timetable.lessons;

  const options = useMemo(() => {
    const values = [...new Set(lessons.map((l) => keyOf(mode, l)).filter(Boolean))];
    if (mode === 'class') return values.sort(compareClasses);
    if (mode === 'room') return values.sort(compareRooms);
    return values.sort((a, b) => a.localeCompare(b, 'ru'));
  }, [lessons, mode]);

  const current = options.includes(selected[mode]) ? selected[mode] : options[0];

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ mode, selected }));
    } catch (e) {
      // приватный режим браузера — просто не запоминаем
    }
  }, [mode, selected]);

  const grid = useMemo(
    () => buildGrid(lessons.filter((l) => keyOf(mode, l) === current)),
    [lessons, mode, current],
  );

  const status = dayStatus(lessons, now);
  const liveNumbers = new Set(
    (status.state === 'lesson' ? status.current : []).map((l) => l.number),
  );
  const isNow = (day, number) => day === now.day && liveNumbers.has(number);

  const choose = (value) => setSelected((prev) => ({ ...prev, [mode]: value }));

  return (
    <div className="tt">
      <div className="tt-toolbar">
        <div className="tt-modes" role="tablist">
          {MODES.map((m) => (
            <button
              key={m.id}
              type="button"
              role="tab"
              aria-selected={mode === m.id}
              className={`tt-mode ${mode === m.id ? 'active' : ''}`}
              onClick={() => setMode(m.id)}
            >
              {m.title}
            </button>
          ))}
        </div>

        {mode === 'class' ? (
          <div className="tt-chips">
            {options.map((value) => (
              <button
                key={value}
                type="button"
                className={`tt-chip ${value === current ? 'active' : ''}`}
                onClick={() => choose(value)}
              >
                {value}
              </button>
            ))}
          </div>
        ) : (
          <select className="tt-select" value={current || ''} onChange={(e) => choose(e.target.value)}>
            {options.map((value) => (
              <option key={value} value={value}>
                {mode === 'room' && /^\d/.test(value) ? `Кабинет ${value}` : value}
              </option>
            ))}
          </select>
        )}
      </div>

      {grid.numbers.length === 0 ? (
        <div className="tt-empty">Нет уроков</div>
      ) : (
        <>
          {/* Компьютер: сетка «дни × уроки» */}
          <div className="tt-grid-wrap">
            <table className="tt-grid">
              <thead>
                <tr>
                  <th className="tt-num-head">Урок</th>
                  {grid.days.map((d) => (
                    <th key={d} className={d === now.day ? 'tt-today' : ''}>
                      {DAY_NAMES[d]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grid.numbers.map((n) => (
                  <tr key={n}>
                    <th className="tt-num">
                      <span className="tt-num-n">{n}</span>
                      <span className="tt-num-time">{grid.times[n]}</span>
                    </th>
                    {grid.days.map((d) => {
                      const cell = grid.cells[`${d}-${n}`];
                      return (
                        <td
                          key={d}
                          className={`${d === now.day ? 'tt-today' : ''} ${isNow(d, n) && cell ? 'tt-now' : ''}`}
                        >
                          {cell ? <LessonCell mode={mode} lessons={cell} /> : null}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Телефон: дни списком, сегодняшний — первым */}
          <div className="tt-days">
            {[...grid.days]
              .sort((a, b) => (a === now.day ? -1 : b === now.day ? 1 : a - b))
              .map((d) => (
                <div key={d} className={`tt-day ${d === now.day ? 'tt-today' : ''}`}>
                  <div className="tt-day-title">
                    {DAY_NAMES[d]}
                    {d === now.day && <span className="tt-day-badge">сегодня</span>}
                  </div>
                  {grid.numbers
                    .filter((n) => grid.cells[`${d}-${n}`])
                    .map((n) => (
                      <div key={n} className={`tt-day-row ${isNow(d, n) ? 'tt-now' : ''}`}>
                        <div className="tt-day-num">
                          <span className="tt-num-n">{n}</span>
                          <span className="tt-num-time">{grid.cells[`${d}-${n}`][0].start}</span>
                        </div>
                        <div className="tt-day-lessons">
                          <LessonCell mode={mode} lessons={grid.cells[`${d}-${n}`]} />
                        </div>
                      </div>
                    ))}
                </div>
              ))}
          </div>
        </>
      )}

      <div className="tt-footer">
        Урок — {timetable.lesson_minutes} минут
        {timetable.updated_at && (
          <>
            {' · '}загружено{' '}
            {new Date(timetable.updated_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}
          </>
        )}
        {' · '}время московское
      </div>
    </div>
  );
}
